import type { ActivityJob, ActivityKey, ActivityStage } from './models';

const ACTIVITY_JOB_IDENTITIES = {
    DocumentImport: {
        stage: 'intake',
        phaseIdentity: 'DocumentImport',
    },
    SharePointImport: {
        stage: 'intake',
        phaseIdentity: 'DocumentImport',
    },
    DocumentClassification: {
        stage: 'classification',
        phaseIdentity: 'DocumentClassification',
    },
    DocumentIntelligence: {
        stage: 'enrichment',
        phaseIdentity: 'DocumentIntelligence',
    },
    DocumentPreview: {
        stage: 'preview',
        phaseIdentity: null,
    },
    SelectedBuildingDocumentExport: { stage: 'export', phaseIdentity: null },
    BuildingDocumentExport: {
        stage: 'export',
        phaseIdentity: null,
    },
    DocumentPackExport: {
        stage: 'export',
        phaseIdentity: null,
    },
} as const satisfies Readonly<
    Record<
        string,
        {
            readonly stage: ActivityStage;
            readonly phaseIdentity: string | null;
        }
    >
>;

type SupportedActivityJobType = keyof typeof ACTIVITY_JOB_IDENTITIES;

export function activityIdentityForJob(
    jobType: string,
    jobId: string,
    correlationId: string | null
): {
    readonly key: ActivityKey;
    readonly stage: ActivityStage;
    readonly phaseIdentity: string | null;
    readonly supported: boolean;
} {
    const policy = isSupportedActivityJobType(jobType)
        ? ACTIVITY_JOB_IDENTITIES[jobType]
        : null;
    const lineageKey: ActivityKey =
        isActivityExportJobType(jobType) || !correlationId
            ? `job:${jobId}`
            : `batch:${correlationId.toLowerCase()}`;
    const key = policy?.phaseIdentity
        ? (`${lineageKey}:${policy.phaseIdentity}` as ActivityKey)
        : lineageKey;

    return {
        key,
        stage: policy?.stage ?? 'generic',
        phaseIdentity: policy?.phaseIdentity ?? null,
        supported: policy !== null,
    };
}

export function isActivityExportJobType(jobType: string): boolean {
    return (
        jobType === 'SelectedBuildingDocumentExport' ||
        jobType === 'BuildingDocumentExport' ||
        jobType === 'DocumentPackExport'
    );
}

export function activityJobTypesSharePhase(
    leftJobType: string,
    rightJobType: string
): boolean {
    const left = activityPhaseIdentity(leftJobType);
    return left !== null && left === activityPhaseIdentity(rightJobType);
}

export function activityItemMatchesIdentity(
    key: ActivityKey,
    correlationId: string | null,
    jobs: readonly Pick<ActivityJob, 'jobId' | 'jobType' | 'stage'>[]
): boolean {
    return (
        jobs.length > 0 &&
        jobs.every((job) => {
            const identity = activityIdentityForJob(
                job.jobType,
                job.jobId,
                correlationId
            );
            return (
                identity.supported &&
                identity.stage === job.stage &&
                identity.key === key
            );
        })
    );
}

function activityPhaseIdentity(jobType: string): string | null {
    return isSupportedActivityJobType(jobType)
        ? ACTIVITY_JOB_IDENTITIES[jobType].phaseIdentity
        : null;
}

function isSupportedActivityJobType(
    jobType: string
): jobType is SupportedActivityJobType {
    return Object.hasOwn(ACTIVITY_JOB_IDENTITIES, jobType);
}
